import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { IoClose, IoMenu } from "react-icons/io5";
import { useMediaQuery } from "react-responsive";
import { Accordion, Dropdown } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import mobileMark from "../assets/figma/mobile-mark.svg";
import companyLogo from "../assets/figma/logo.svg";
import arrowIcon from "../assets/figma/nav-arrow.svg";
import TranslatorComponent from "./TranslatorComponent";

import "../css/Header.css";

function Header() {
  const { t } = useTranslation();

  const isMobile = useMediaQuery({ query: "(max-width: 1150px)" });
  const isDesktop = !isMobile;
  const location = useLocation();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    setIsMenuOpen(false);
    setShowDropdown(false);
  }, [location.pathname, isMobile]);

  useEffect(() => {
    if (!isMenuOpen && !showDropdown) return;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setIsMenuOpen(false);
        setShowDropdown(false);
      }
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [isMenuOpen, showDropdown]);

  const investorItems = [
    {
      label: t("main.header.servicesDropdown.directorDealings"),
      path: "/director-dealings",
    },
    {
      label: t("main.header.servicesDropdown.analystReports"),
      path: "/analyst-reports",
    },
    {
      label: t("main.header.servicesDropdown.financialCalendar"),
      path: "/financial-calendar",
    },
    {
      label: t("main.header.servicesDropdown.financialReports"),
      path: "/financial-reports",
    },
    {
      label: t("main.header.servicesDropdown.corporateActions"),
      path: "/corporate-actions",
    },
    {
      label: t("main.header.shareholdersMeetings"),
      path: "/shareholders-meetings",
    },
  ];

  const closeDropdown = () => {
    setShowDropdown(false);
  };

  const closeMobileMenu = () => {
    if (isMobile) {
      setIsMenuOpen(false);
    }
  };

  const closeAllMenus = () => {
    closeMobileMenu();
    closeDropdown();
  };

  const toggleMobileMenu = () => {
    setIsMenuOpen((prev) => !prev);
  };

  return (
    <header className="header">
      <nav className="nav">
        <NavLink to="/" className="nav__logo" onClick={closeAllMenus}>
          <img src={isMobile ? mobileMark : companyLogo} alt="Rigsave logo" />
        </NavLink>

        {isMobile && (
          <button
            type="button"
            className="nav__toggle"
            onClick={toggleMobileMenu}
            aria-label={
              isMenuOpen
                ? t("main.header.closeMenu")
                : t("main.header.openMenu")
            }
            aria-expanded={isMenuOpen}
            aria-controls="site-navigation"
          >
            {isMenuOpen ? <IoClose /> : <IoMenu />}
          </button>
        )}

        <div id="site-navigation" className={`nav__menu ${isMenuOpen ? "show-menu" : ""}`}>
          {isMobile && (
            <div className="mobile-menu-header">
              <img src={isMobile ? mobileMark : companyLogo} alt="Rigsave logo" />

              <button
                type="button"
                className="nav__close"
                onClick={() => setIsMenuOpen(false)}
                aria-label={t("main.header.closeMenu")}
              >
                <IoClose />
              </button>
            </div>
          )}

          <ul className={isMobile ? "nav__list" : "nav__list__web"}>
            {isMobile && (
              <li>
                <NavLink to="/" className="nav__link" onClick={closeMobileMenu}>
                  {t("main.header.home")}
                </NavLink>
              </li>
            )}

            <li>
              <NavLink
                to="/about-us"
                className="nav__link"
                onClick={closeAllMenus}
              >
                {t("main.header.aboutUs")}
              </NavLink>
            </li>

            <li>
              <NavLink
                to="/services"
                className="nav__link"
                onClick={closeAllMenus}
              >
                {t("main.header.services")}
              </NavLink>
            </li>

            {isDesktop && (
              <li
                className="investor-dropdown-wrapper"
                onMouseEnter={() => setShowDropdown(true)}
                onMouseLeave={() => setShowDropdown(false)}
              >
                <Dropdown className="investor-dropdown" show={showDropdown}
                  onToggle={(next, metadata) => {
                    if (metadata.source !== "click") setShowDropdown(next);
                  }}>
                  <Dropdown.Toggle
                    className="services-toggle"
                    id="dropdown-services"
                    onClick={(event) => {
                      event.preventDefault();
                      setShowDropdown(true);
                    }}
                    aria-expanded={showDropdown}
                  >
                    <span>
                      {t("main.header.servicesDropdown.investorRelations")}
                    </span>

                    <img
                      src={arrowIcon}
                      alt=""
                      className="dropdown-icon"
                      aria-hidden="true"
                    />
                  </Dropdown.Toggle>

                  <Dropdown.Menu className="services-menu">
                    {investorItems.map((item) => (
                      <Dropdown.Item
                        as={Link}
                        to={item.path}
                        key={item.path}
                        onClick={closeAllMenus}
                      >
                        {item.label}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </li>
            )}

            {isMobile && (
              <li>
                <Accordion className="accordion-mobile-header">
                  <Accordion.Item eventKey="0">
                    <Accordion.Header>
                      {t("main.header.servicesDropdown.investorRelations")}
                    </Accordion.Header>

                    <Accordion.Body>
                      {investorItems.map((item) => (
                        <Link
                          to={item.path}
                          onClick={closeMobileMenu}
                          key={item.path}
                        >
                          {item.label}
                        </Link>
                      ))}
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
              </li>
            )}

            <li>
              <NavLink
                to="/contact-us"
                className="nav__link"
                onClick={closeAllMenus}
              >
                {t("main.header.contacts")}
              </NavLink>
            </li>

            <li className="translator-item">
              <TranslatorComponent />
            </li>
          </ul>
        </div>
      </nav>
    </header>
  );
}

export default Header;